/* eslint-disable check-file/filename-naming-convention */
import { useLatest } from 'ahooks';
import { useEffect, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
/* @ts-expect-error 图片路径 */
import cursorPng from './cursor.png';

const cursorSize = 20; // 光标图片宽高

const useMouseWheel = ({
    wrapperElRef,
    inputElRef,
    max,
    min,
    inputValue,
    onChangeInputValue,
    onChangeAfter,
    onMouseWheelEnd,
    enableMouseWheel = false,
    mouseWheelRatio = 1,
    disabled = false,
}: {
    wrapperElRef: React.RefObject<HTMLDivElement>;
    inputElRef: React.RefObject<HTMLInputElement>;
    max?: number;
    min?: number;
    inputValue: number;
    onChangeInputValue?: (value: number) => void;
    onChangeAfter?: (value: number) => void;
    onMouseWheelEnd?: () => void;
    enableMouseWheel?: boolean;
    mouseWheelRatio?: number;
    disabled?: boolean;
}) => {
    const [isFocus, setIsFocus] = useState(false);
    const [isDragging, setIsDragging] = useState(false);

    const customCursorRef = useRef<HTMLDivElement | null>(null);

    const createCursor = () => {
        const cursor = document.createElement('div');
        cursor.style.cssText = `
        position: fixed;
        width: ${cursorSize}px;
        height: ${cursorSize}px;
        background: url(${cursorPng}) no-repeat center center;
        background-size: contain;
        pointer-events: none;
        z-index: 9999;
        transform: translate3d(0, 0, 0);
        left: 0;
        top: 0;
        will-change: transform;
        backface-visibility: hidden;
      `;
        document.body.appendChild(cursor);
        return cursor;
    };

    useEffect(() => {
        if (disabled) return;
        if (!enableMouseWheel) return;

        const wrapperEl = wrapperElRef.current;

        if (!wrapperEl) return;
        if (isFocus) return;

        const tempId = uuidv4();
        wrapperEl.dataset.tempId = tempId;
        const removeStyleSheet = registerStyleSheet(`
          [data-temp-id="${tempId}"] *:not(.input-addon-after, .input-addon-after *, .input-addon-before, .input-addon-before *) {
                pointer-events: none;
                user-select: none;
                cursor: ew-resize;
            }

            [data-temp-id="${tempId}"] {
                cursor: ew-resize;
                ${isDragging && 'border: 1px solid var(--stroke-input-selected, #38f);'}
            }
        `);

        return removeStyleSheet;
    }, [enableMouseWheel, isFocus, isDragging, disabled]);

    const onWrapperClick = (e: Pick<MouseEvent, 'clientX' | 'clientY'>) => {
        if (disabled) return;
        setIsFocus(true);
        inputElRef.current?.focus();
    };

    const onInputBlur = () => setIsFocus(false);

    const inputValueRef = useLatest(inputValue);

    const onWrapperMouseDown = (e: Pick<MouseEvent, 'clientX' | 'clientY' | 'target' | 'stopPropagation'>) => {
        if (disabled) return;
        e.stopPropagation();

        // 检查点击是否发生在 arrows 区域
        const isArrowsClick = (e.target as HTMLElement)?.closest('.arrows');

        if (enableMouseWheel && !isFocus && !isArrowsClick) {
            const startValue = inputValueRef.current;

            let resultValue: number;
            let hasMove = false;

            // 计算鼠标相对于自定义光标图片中心的偏移

            let cursorX = e.clientX - cursorSize / 2;
            let cursorY = e.clientY - cursorSize / 2;

            let accumulatedDelta = 0;

            const onWindowMouseMove = (e: MouseEvent) => {
                // 忽略异常的大幅度移动
                if (Math.abs(e.movementX) > 100) {
                    return;
                }

                // 首次移动时创建光标
                if (!customCursorRef.current) {
                    customCursorRef.current = createCursor();
                    customCursorRef.current.style.transform = `translate(${cursorX}px, ${cursorY}px)`;
                }
                wrapperElRef.current?.requestPointerLock();

                hasMove = true;
                setIsDragging(true);

                // 更新自定义光标位置，考虑偏移量
                if (customCursorRef.current) {
                    cursorX += e.movementX;
                    cursorY += e.movementY;

                    // 处理光标循环
                    if (cursorX >= window.innerWidth) {
                        cursorX = 0;
                    } else if (cursorX <= 0) {
                        cursorX = window.innerWidth;
                    }

                    if (cursorY >= window.innerHeight) {
                        cursorY = 0;
                    } else if (cursorY <= 0) {
                        cursorY = window.innerHeight;
                    }
                    customCursorRef.current.style.transform = `translate(${cursorX}px, ${cursorY}px)`;
                }

                // 优化值的计算和更新
                accumulatedDelta += e.movementX * mouseWheelRatio;
                let value = startValue + Math.round(accumulatedDelta);

                if (typeof min === 'number') {
                    value = Math.max(value, min);
                }

                if (typeof max === 'number') {
                    value = Math.min(value, max);
                }

                resultValue = value;

                onChangeInputValue?.(value);
            };

            window.addEventListener('mousemove', onWindowMouseMove);

            const onWindowMouseUp = () => {
                if (hasMove) {
                    onMouseWheelEnd?.();
                    onChangeAfter?.(resultValue);
                    hasMove = false;
                }
                setIsDragging(false);
                document.exitPointerLock();
                // 移除光标
                if (customCursorRef.current) {
                    document.body.removeChild(customCursorRef.current);
                    customCursorRef.current = null;
                }

                window.removeEventListener('mousemove', onWindowMouseMove);
                window.removeEventListener('mouseup', onWindowMouseUp);
            };

            window.addEventListener('mouseup', onWindowMouseUp);
        }
    };

    const onWrapperTouchStart = (e: React.TouchEvent) => {
        if (disabled) return;

        const isArrowsClick = (e.target as HTMLElement)?.closest('.arrows');
        if (enableMouseWheel && !isFocus && !isArrowsClick) {
            const startValue = inputValueRef.current;
            let resultValue: number;
            let hasMove = false;

            let accumulatedDelta = 0;
            const touch = e.touches[0];
            let lastTouchX = touch.clientX;
            let lastTouchY = touch.clientY;

            // 创建自定义光标
            let cursorX = lastTouchX - cursorSize / 2;
            let cursorY = lastTouchY - cursorSize / 2;

            if (!customCursorRef.current) {
                customCursorRef.current = createCursor();
                customCursorRef.current.style.transform = `translate(${cursorX}px, ${cursorY}px)`;
            }

            setIsDragging(true);

            const onTouchMove = (e: TouchEvent) => {
                const touch = e.touches[0];
                const movementX = touch.clientX - lastTouchX;
                const movementY = touch.clientY - lastTouchY;

                lastTouchX = touch.clientX;
                lastTouchY = touch.clientY;

                // 忽略异常大幅移动
                if (Math.abs(movementX) > 100) return;

                // 更新光标位置
                cursorX += movementX;
                cursorY += movementY;

                if (customCursorRef.current) {
                    customCursorRef.current.style.transform = `translate(${cursorX}px, ${cursorY}px)`;
                }

                hasMove = true;
                accumulatedDelta += movementX * mouseWheelRatio;

                let value = startValue + Math.round(accumulatedDelta);
                if (typeof min === 'number') value = Math.max(value, min);
                if (typeof max === 'number') value = Math.min(value, max);

                resultValue = value;
                onChangeInputValue?.(value);
            };

            const onTouchEnd = () => {
                if (hasMove) {
                    onMouseWheelEnd?.();
                    onChangeAfter?.(resultValue);
                    hasMove = false;
                }

                setIsDragging(false);

                if (customCursorRef.current) {
                    document.body.removeChild(customCursorRef.current);
                    customCursorRef.current = null;
                }

                window.removeEventListener('touchmove', onTouchMove);
                window.removeEventListener('touchend', onTouchEnd);
            };

            window.addEventListener('touchmove', onTouchMove, { passive: false });
            window.addEventListener('touchend', onTouchEnd, { passive: false });
        }
    };

    return {
        onWrapperClick,
        onInputBlur,
        onWrapperMouseDown,
        onWrapperTouchStart,
    };
};

export default useMouseWheel;

/** 判断click事件是否只是点击, 不包括拖动 */
const isOnlyClick = (() => {
    const mouseDownPoint: {
        clientX: number | null;
        clientY: number | null;
    } = {
        clientX: null,
        clientY: null,
    };
    document.querySelector('body')?.addEventListener(
        'mousedown',
        (e) => {
            mouseDownPoint.clientX = e.clientX;
            mouseDownPoint.clientY = e.clientY;
        },
        false,
    );

    const isOnlyClick = (clickEndPoint: { clientX: number; clientY: number }) => {
        const { clientX, clientY } = clickEndPoint;
        const { clientX: mouseDownClientX, clientY: mouseDownClientY } = mouseDownPoint;
        const isOnlyClick = clientX === mouseDownClientX && clientY === mouseDownClientY;
        return isOnlyClick;
    };
    return isOnlyClick;
})();

const registerStyleSheet = (styleCode: string) => {
    const style = document.createElement('style');
    style.innerHTML = styleCode;
    document.body.appendChild(style);
    const remove = () => {
        document.body.removeChild(style);
    };
    return remove;
};
